import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCopy, FiLink, FiChevronDown, FiCheck, FiZap, FiExternalLink } from 'react-icons/fi';
import './ReferralGenerator.css';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const ReferralGenerator = () => {
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [vpa, setVpa] = useState('');
  const navigate = useNavigate();
  const [referralLink, setReferralLink] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch offers from API
        const response = await axios.post('https://offers.cashbolt.in.net/server/', {
          action: 'getActiveOffers'
        });

        if (response.data.status) {
          setOffers(response.data.data);
          if (response.data.data.length > 0) {
            setSelectedOffer(response.data.data[0]); // Set first offer as default
          }
        } else {
          throw new Error(response.data.message || 'Failed to fetch offers');
        }
      } catch (err) {
        console.error("Error fetching offers:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOffers();
  }, []);

  const redirectTo = (url) => {
    window.open(url);
  };

  const generateReferralLink = async () => {
    if (!selectedOffer || !vpa) {
      alert('Please select an offer and enter your VPA');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Send VPA to server and get referral token
      const response = await axios.post('https://offers.cashbolt.in.net/server/', {
        action: 'generateReferralToken',
        offerId: selectedOffer.offerId,
        vpa: vpa
      });

      if (response.data.status) {
        const token = response.data.token;
        const link = `https://${window.location.hostname}/offer/${selectedOffer.offerId}/${token}`;
        setReferralLink(link);
      } else {
        throw new Error(response.data.message || 'Failed to generate referral');
      }
    } catch (err) {
      console.error("Error generating referral:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-pulse"></div>
      </div>
    );
  }

  return (
    <div className="referral-page">
      {/* Animated background elements */}
      <motion.div
        className="bg-shine"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.1 }}
        transition={{ duration: 1.5 }}
      />

      {/* Main container */}
      <motion.div
        className="referral-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* Page header */}
        <div className="referral-header">
          <motion.div
            className="referral-icon"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <FiLink />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h1>Generate Referral Link</h1>
            <p className="referral-subtitle">Earn commissions by sharing offers</p>
          </motion.div>
        </div>

        {/* Generated link - moved to top */}
        {referralLink && (
          <motion.div
            className="link-container"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            transition={{ duration: 0.4 }}
          >

            <div className="link-display">
              <motion.div className="input-field"
                whileHover={{ y: -2 }}>
              <input
                type="text"
                value={referralLink}
                readOnly
                className="generated-link"
              />
              </motion.div>
              <button
                className="copy-btn"
                onClick={copyToClipboard}
                aria-label="Copy to clipboard"
              >
                {copied ? <FiCheck /> : <FiCopy />}
                <span>{copied ? 'Copied!' : 'Copy'}</span>
              </button>
            </div>
            <p className="link-instruction">
              Share this link with friends to earn ₹{selectedOffer.amount} per successful referral
            </p>
          </motion.div>
        )}

        {/* Only show these elements if referral link is not generated */}
        {!referralLink && (
          <>
            {/* Custom select dropdown */}
            <motion.div
              className="custom-select-container"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <div
                className="custom-select-header"
                onClick={() => setIsOpen(!isOpen)}
              >
                {selectedOffer && (
                  <div className="selected-offer">
                    <div
                      className="offer-logo"
                      style={{ backgroundColor: selectedOffer.color }}
                    >
                      {selectedOffer.name.charAt(0)}
                    </div>
                    <div className="offer-details">
                      <h3>{selectedOffer.name}</h3>
                      <p>₹{selectedOffer.amount.split(',').slice(-1)[0]} per referral</p>
                    </div>
                  </div>
                )}
                <motion.div
                  animate={{ rotate: isOpen ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <FiChevronDown />
                </motion.div>
              </div>

              <AnimatePresence>
                {isOpen && (
                  <motion.div
                    className="custom-select-options"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {offers.map((offer) => (
                      <div
                        key={offer.offerId}
                        className={`offer-option ${selectedOffer?.offerId === offer.offerId ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedOffer(offer);
                          setIsOpen(false);
                        }}
                      >
                        <div
                          className="offer-logo"
                          style={{ backgroundColor: offer.color }}
                        >
                          {offer.name.charAt(0)}
                        </div>
                        <div className="offer-details">
                          <h3>{offer.name}</h3>
                          <p>₹{offer.amount.split(',').slice(-1)[0]} on completion</p>
                        </div>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* VPA input */}
            <motion.div
              className="input-wrapper"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <div className="input-container">
                <label htmlFor="vpa">Your Votex VPA</label>
                <motion.div
                  className="input-field"
                  whileHover={{ y: -2 }}
                >
                  <input
                    type="text"
                    id="vpa"
                    value={vpa}
                    onChange={(e) => setVpa(e.target.value)}
                    placeholder="yourname@votex"
                    required
                  />
                </motion.div>
              </div>
            </motion.div>

            {/* Generate button */}
            <motion.button
              className="generate-btn"
              onClick={generateReferralLink}
              disabled={!selectedOffer || !vpa}
              whileHover={{
                y: -2,
                boxShadow: '0 8px 25px rgba(139, 92, 246, 0.5)'
              }}
              whileTap={{ scale: 0.98 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
            >
              Generate Referral Link
            </motion.button>
          </>
        )}

        {/* Wallet CTA */}
        <motion.div
          className="wallet-cta"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <div className="wallet-content">
            <h3>Instant Payouts</h3>
            <p>Use CBPocket for faster rewards</p>
          </div>
          <motion.button
            className="wallet-btn"
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => redirectTo('https://cbpocket.site/')}
          >
            <FiZap /> Get Wallet
          </motion.button>
        </motion.div>

        {/* Terms section */}
        <motion.div
          className="terms-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
        >
          <h3>How it works</h3>
          <ul className="terms-list">
            <li>Select an offer from the dropdown</li>
            <li>Enter your Votex VPA for payment</li>
            <li>Generate and share your unique referral link</li>
            <li>Earn commission when friends complete the offer</li>
            <li>Payments processed within 24 hours</li>
          </ul>
        </motion.div>

      </motion.div>
      <motion.button
        className="track-lead-btn"
        onClick={() => navigate('/track')}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.2 }}
        whileHover={{
          x: -5,
          boxShadow: '0 5px 15px rgba(139, 92, 246, 0.4)'
        }}
        whileTap={{ scale: 0.95 }}
      >
        <FiExternalLink /> Track Lead
      </motion.button>
    </div>
  );
};

export default ReferralGenerator;